import { PatientRepository } from "@/domain/repository/patient-repository";
import { formatPhoneForServer, validatePhoneNumber } from "@/utils/phone-number-utils";
import { SATError, SATErrorType, SATResult } from "@sadtx/sat-error";
import { translate } from "@/utils/i18n";

export type CreatePatientPayload = {
  timeZone: string;
  email?: string;
  name: string;
  phone: string;
  countryCode: string;
  detail: {
    gender: string;
    birthday: string;
    memo?: string | null;
    isTestAccount: boolean;
    doctorUuid?: string;
  };
};

export type CreatePatientValidationErrors = Partial<
  Record<"timeZone" | "email" | "name" | "phone" | "countryCode" | "birthday" | "gender", string>
>;

export class CreatePatientValidationError extends SATError {
  constructor(public readonly fieldErrors: CreatePatientValidationErrors) {
    super(SATErrorType.ValidationError, translate("patient.validation.title"));
  }
}

export class CreatePatientUsecase {
  constructor(private readonly patientRepo: PatientRepository) {}

  validate(payload: CreatePatientPayload): CreatePatientValidationErrors {
    const errors: CreatePatientValidationErrors = {};
    const timeZone = payload.timeZone.trim();
    const email = payload.email?.trim().toLowerCase() ?? "";
    const name = payload.name.trim();
    const phone = payload.phone.trim();
    const countryCode = payload.countryCode.trim();
    const gender = payload.detail.gender.trim();
    const birthday = payload.detail.birthday.trim();

    if (!timeZone) {
      errors.timeZone = translate("patient.validation.timeZoneRequired");
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = translate("patient.validation.emailFormat");
    }

    if (!name) {
      errors.name = translate("patient.validation.nameRequired");
    } else if (name.length <= 1) {
      errors.name = translate("patient.validation.nameMinLength");
    }

    if (!countryCode) {
      errors.countryCode = translate("patient.validation.countryCodeRequired");
    }

    const phoneError = validatePhoneNumber(phone, countryCode);
    if (phoneError) {
      errors.phone = phoneError;
    }

    if (!gender) {
      errors.gender = translate("patient.validation.genderRequired");
    }

    if (!birthday) {
      errors.birthday = translate("patient.validation.dateRequired");
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
      errors.birthday = translate("patient.validation.dateFormatDash");
    } else if (Number.isNaN(new Date(birthday).getTime())) {
      errors.birthday = translate("patient.validation.dateInvalid");
    } else if (new Date(birthday).getTime() > Date.now()) {
      errors.birthday = translate("patient.validation.birthdayFuture");
    }

    return errors;
  }

  async execute(payload: CreatePatientPayload): Promise<SATResult<void, SATError>> {
    const normalizedPayload: CreatePatientPayload = {
      timeZone: payload.timeZone.trim(),
      ...(payload.email?.trim()
        ? { email: payload.email.trim().toLowerCase() }
        : {}),
      name: payload.name.trim(),
      countryCode: payload.countryCode.trim(),
      phone: formatPhoneForServer(payload.phone, payload.countryCode.trim()),
      detail: {
        gender: payload.detail.gender.trim(),
        birthday: payload.detail.birthday.trim(),
        memo: payload.detail.memo?.trim() ?? "",
        isTestAccount: payload.detail.isTestAccount,
        ...(payload.detail.doctorUuid === undefined
          ? {}
          : { doctorUuid: payload.detail.doctorUuid.trim() }),
      },
    };
    const validationErrors = this.validate(normalizedPayload);
    if (Object.keys(validationErrors).length > 0) {
      return SATResult.failure(new CreatePatientValidationError(validationErrors));
    }

    return this.patientRepo.createPatient(normalizedPayload);
  }
}
